import ModalButton from "@/Components/ModalButton"
import { resolveBackgroundUrl } from "@/utils/gameBackgrounds"
import { useAppSelector } from "@/store/hooks"
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid"
import { useNavigate } from "react-router-dom"

interface GameNotAvailableProps {
  // Callers outside the game page (the lobby) pass their own background so the
  // screen keeps looking like the page the player was on.
  backgroundUrl?: string
}

const GameNotAvailable: React.FC<GameNotAvailableProps> = ({
  backgroundUrl: backgroundUrlProp,
}) => {
  const nav = useNavigate()
  // Match the game page: use the player's chosen (persisted) background,
  // falling back to the default.
  const selectedTheme = useAppSelector((state) => state.global.selectedTheme)
  const backgroundUrl =
    backgroundUrlProp || resolveBackgroundUrl(selectedTheme?.id)

  return (
    <div
      data-testid="game.not-available"
      className="w-screen h-screen bg-cover bg-center flex items-center justify-center overflow-hidden"
      style={{ backgroundImage: `url(${backgroundUrl})` }}
      onError={(e) => {
        e.currentTarget.style.backgroundImage = `url(${resolveBackgroundUrl()})`
      }}
    >
      <div className="flex flex-col items-center gap-6 px-8 py-10 rounded-2xl bg-black/50 backdrop-blur-xl border border-white/15 shadow-2xl max-w-md mx-4 text-center">
        <div className="flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-br from-yellow-500 via-yellow-600 to-orange-600 shadow-lg">
          <ExclamationTriangleIcon className="h-9 w-9 text-white" />
        </div>

        <div className="flex flex-col gap-2">
          <h2 className="font-poppins text-2xl font-bold text-white">
            Game not available
          </h2>
          <p className="font-poppins text-base text-gray-300">
            This game could not be found. It may have ended, or you may not be a
            part of it.
          </p>
        </div>

        <div className="flex items-center justify-center gap-4 flex-wrap">
          <ModalButton
            size="md"
            color="default"
            onClick={() => nav(-1)}
            data-testid="game.not-available-back-btn"
          >
            <div className="flex items-center gap-2">
              <i className="text-white fa-solid fa-arrow-left"></i>
              <span className="text-white font-medium font-poppins text-lg">
                Back
              </span>
            </div>
          </ModalButton>
          <ModalButton
            size="md"
            color="blue"
            onClick={() => nav("/lobby")}
            data-testid="game.not-available-lobby-btn"
          >
            <div className="flex items-center gap-2">
              <i className="text-white fa-solid fa-house"></i>
              <span className="text-white font-medium font-poppins text-lg">
                Lobby
              </span>
            </div>
          </ModalButton>
        </div>
      </div>
    </div>
  )
}

export default GameNotAvailable
